import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { mediaUrl } from '../services/api';
import { getCaseMediaPlayback, inferMediaKind } from '../services/caseMedia';

const legacyUrl = (media) => media?.url || mediaUrl(media?.path) || '';

const CaseMediaPlayer = ({ media, compact = false, autoPlay = false, sx = {} }) => {
  const [source, setSource] = useState('');
  const [poster, setPoster] = useState('');
  const [loading, setLoading] = useState(Boolean(media?.assetId || media?.id));
  const [error, setError] = useState('');
  const kind = useMemo(() => inferMediaKind(media), [media]);
  const assetId = media?.assetId || media?.id;

  useEffect(() => {
    let active = true;
    setError('');
    if (!assetId) {
      setSource(legacyUrl(media));
      setPoster('');
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    Promise.all([
      getCaseMediaPlayback(media),
      kind === 'video' && media?.hasPoster
        ? getCaseMediaPlayback(media, 'poster').catch(() => null)
        : Promise.resolve(null),
    ]).then(([playback, posterPlayback]) => {
      if (!active) return;
      setSource(playback?.url || '');
      setPoster(posterPlayback?.url || '');
    }).catch((requestError) => {
      if (active) setError(requestError.response?.data?.message || requestError.message || 'Media is unavailable.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [assetId, kind, media]);

  if (loading) {
    return <Box sx={{ minHeight: compact ? 90 : 220, display: 'grid', placeItems: 'center', ...sx }}><CircularProgress size={28} /></Box>;
  }
  if (error || !source) {
    return <Alert severity="warning" sx={sx}>{error || 'Media is unavailable.'}</Alert>;
  }

  const commonSx = {
    display: 'block',
    width: '100%',
    maxHeight: compact ? 180 : '72vh',
    borderRadius: 2,
    bgcolor: '#081423',
    ...sx,
  };
  if (kind === 'image') {
    return <Box component="img" src={source} alt={media?.displayName || media?.originalName || 'Case media'} sx={{ ...commonSx, objectFit: 'contain' }} />;
  }
  if (kind === 'video') {
    return <Box component="video" src={source} poster={poster || undefined} controls playsInline preload="metadata" autoPlay={autoPlay} sx={commonSx} />;
  }
  if (kind === 'audio') {
    return <Box component="audio" src={source} controls preload="metadata" autoPlay={autoPlay} sx={{ width: '100%', ...sx }} />;
  }
  return null;
};

export default CaseMediaPlayer;
